import axios from 'axios';
import type { AuthResponse, GlobalSearchResponse, OpencodeHistoryItem, OpencodeStatus, SelectedGlobalContext, Session, SessionContextResponse } from '../types';

axios.defaults.withCredentials = true;

export async function getCurrentUser(): Promise<AuthResponse> {
  const response = await axios.get<AuthResponse>('/api/auth/me');
  return response.data;
}

export async function login(identity: string, password: string): Promise<AuthResponse> {
  const response = await axios.post<AuthResponse>('/api/auth/login', { identity, password });
  return response.data;
}

export async function logout(): Promise<void> {
  await axios.post('/api/auth/logout');
}

export async function getHealth(): Promise<OpencodeStatus> {
  const response = await axios.get('/api/health', { timeout: 5000 });
  return { checked: true, ok: Boolean(response.data.ok), url: response.data.url ?? null, error: null };
}

export async function listSessions(): Promise<Session[]> {
  const response = await axios.get<Session[]>('/api/sessions');
  return Array.isArray(response.data) ? response.data : [];
}

export async function createSession(name: string, context?: SelectedGlobalContext | null): Promise<Session> {
  const response = await axios.post<Session | Session[]>('/api/sessions', { name, context });
  return Array.isArray(response.data) ? response.data[0] : response.data;
}

export async function deleteSession(id: string): Promise<void> {
  await axios.delete(`/api/sessions/${id}`);
}

export async function listMessages(sessionId: string): Promise<OpencodeHistoryItem[]> {
  const response = await axios.get<OpencodeHistoryItem[]>(`/api/sessions/${sessionId}/messages`);
  return Array.isArray(response.data) ? response.data : [];
}

export async function getSessionContext(sessionId: string): Promise<SessionContextResponse> {
  const response = await axios.get<SessionContextResponse>(`/api/sessions/${sessionId}/context`);
  return response.data;
}

export async function sendChatMessage(sessionId: string, message: string): Promise<OpencodeHistoryItem> {
  const response = await axios.post<OpencodeHistoryItem>('/api/chat', { sessionId, message });
  return response.data;
}

export async function searchGlobal(query: string): Promise<GlobalSearchResponse> {
  const response = await axios.get<GlobalSearchResponse>('/api/global-search', {
    params: { q: query },
    timeout: 20000,
  });
  return {
    query: response.data.query || query,
    results: Array.isArray(response.data.results) ? response.data.results : [],
    warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
  };
}

export async function searchCompanyWorkspaces(companyId: string, query: string): Promise<GlobalSearchResponse> {
  const response = await axios.get<GlobalSearchResponse>('/api/context-search', {
    params: { target: 'workspaces', companyId, q: query },
    timeout: 20000,
  });
  return {
    query: response.data.query || query,
    results: Array.isArray(response.data.results) ? response.data.results : [],
    warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
  };
}

export async function searchUserCompanies(user: { email?: string | null; userId?: string | null }, query: string): Promise<GlobalSearchResponse> {
  const response = await axios.get<GlobalSearchResponse>('/api/context-search', {
    params: { target: 'companies', userEmail: user.email || undefined, userId: user.userId || undefined, q: query },
    timeout: 20000,
  });
  return {
    query: response.data.query || query,
    results: Array.isArray(response.data.results) ? response.data.results : [],
    warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
  };
}

export async function searchWorkspaceUsers(workspaceId: string, query: string): Promise<GlobalSearchResponse> {
  const response = await axios.get<GlobalSearchResponse>('/api/context-search', {
    params: { target: 'users', workspaceId, q: query },
    timeout: 20000,
  });
  return {
    query: response.data.query || query,
    results: Array.isArray(response.data.results) ? response.data.results : [],
    warnings: Array.isArray(response.data.warnings) ? response.data.warnings : [],
  };
}
